import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Container,
  Typography,
  Chip,
  Button,
  Grid,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';

import Iconify from '../../components/iconify';
import { API_URL } from '../../api';

const cookies = new Cookies();

export default function OrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState('');

  // Dialog states
  const [resendDialog, setResendDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);
  const [regenerateTokens, setRegenerateTokens] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/admin/${id}`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      setOrder(response.data.data);
      setNotes(response.data.data.notes || '');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order');
      navigate('/orders');
    }
  };

  const formatPrice = (price) => {
    return `£${(price / 100).toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleResendEmail = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/orders/admin/${id}/resend-email`,
        { regenerateTokens },
        {
          headers: {
            Authorization: cookies.get('TOKEN'),
          },
        }
      );
      toast.success('Email sent successfully');
      setResendDialog(false);
      fetchOrder(); // Refresh order data
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.response?.data?.message || 'Failed to send email');
    }
    setActionLoading(false);
  };

  const handleRefund = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/orders/admin/${id}/refund`,
        { reason: refundReason },
        {
          headers: {
            Authorization: cookies.get('TOKEN'),
          },
        }
      );
      toast.success('Refund processed successfully');
      setRefundDialog(false);
      fetchOrder(); // Refresh order data
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(error.response?.data?.message || 'Failed to process refund');
    }
    setActionLoading(false);
  };

  const handleSaveNotes = async () => {
    try {
      await axios.patch(
        `${API_URL}/api/orders/admin/${id}/notes`,
        { notes },
        {
          headers: {
            Authorization: cookies.get('TOKEN'),
          },
        }
      );
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Alert severity="error">Order not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order {order.orderNumber} | English Teacher Admin</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Button
              startIcon={<Iconify icon="mdi:arrow-left" />}
              onClick={() => navigate('/orders')}
              sx={{ mb: 1 }}
            >
              Back to Orders
            </Button>
            <Typography variant="h4" sx={{ fontFamily: 'Inter', fontWeight: '600' }}>
              Order {order.orderNumber}
            </Typography>
          </Box>
          <Box>
            {order.paymentStatus === 'paid' && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="mdi:email-outline" />}
                  onClick={() => setResendDialog(true)}
                  sx={{ mr: 1 }}
                >
                  Resend Email
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Iconify icon="mdi:cash-refund" />}
                  onClick={() => setRefundDialog(true)}
                >
                  Refund
                </Button>
              </>
            )}
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {/* Order Status */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={order.paymentStatus}
                    color={getStatusColor(order.paymentStatus)}
                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Placed on {formatDate(order.createdAt)}
                  </Typography>
                  {order.refundedAt && (
                    <Typography variant="body2" color="error">
                      Refunded on {formatDate(order.refundedAt)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {order.customer.firstName} {order.customer.lastName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {order.customer.email}
                </Typography>
                {order.customer.buyerType && (
                  <Chip
                    label={order.customer.buyerType}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Payment Method
                  </Typography>
                  <Chip
                    icon={<Iconify icon={order.paymentMethod === 'stripe' ? 'mdi:credit-card' : 'mdi:paypal'} />}
                    label={order.paymentMethod}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
                {order.stripePaymentIntentId && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Stripe ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {order.stripePaymentIntentId.slice(0, 20)}...
                    </Typography>
                  </Box>
                )}
                {order.refundAmount && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Refund Amount
                    </Typography>
                    <Typography variant="body2" color="error">
                      {formatPrice(order.refundAmount)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Order Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>File Type</TableCell>
                        <TableCell align="right">Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>
                            <Chip label={item.fileType.toUpperCase()} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatPrice(item.price)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatPrice(order.total)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Download Tokens */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Download Links
                </Typography>
                {order.downloadLinks && order.downloadLinks.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Downloads</TableCell>
                          <TableCell>Expires</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.downloadLinks.map((link, index) => (
                          <TableRow key={index}>
                            <TableCell>{link.title}</TableCell>
                            <TableCell>
                              {link.isExpired ? (
                                <Chip label="Expired" color="error" size="small" />
                              ) : link.isExhausted ? (
                                <Chip label="Limit Reached" color="warning" size="small" />
                              ) : (
                                <Chip label="Active" color="success" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              {link.downloadCount} / {link.maxDownloads}
                            </TableCell>
                            <TableCell>{formatDate(link.expiresAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No download links generated yet</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Admin Notes */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Admin Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal notes about this order..."
                />
                <Button variant="outlined" sx={{ mt: 2 }} onClick={handleSaveNotes}>
                  Save Notes
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Resend Email Dialog */}
        <Dialog open={resendDialog} onClose={() => setResendDialog(false)}>
          <DialogTitle>Resend Order Email</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will send a new order confirmation email to {order.customer.email}.
            </DialogContentText>
            <FormControlLabel
              control={
                <Checkbox
                  checked={regenerateTokens}
                  onChange={(e) => setRegenerateTokens(e.target.checked)}
                />
              }
              label="Generate new download links (invalidates old links)"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResendDialog(false)}>Cancel</Button>
            <Button onClick={handleResendEmail} variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Refund Dialog */}
        <Dialog open={refundDialog} onClose={() => setRefundDialog(false)}>
          <DialogTitle>Process Refund</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will refund {formatPrice(order.total)} to the customer and invalidate their download links.
              This action cannot be undone.
            </DialogContentText>
            <TextField
              fullWidth
              label="Refund Reason (optional)"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
            <Button onClick={handleRefund} variant="contained" color="error" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Process Refund'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
